import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const helpers = createMultiStyleConfigHelpers([
  'root',
  'grid',
  'mediaRegion',
  'contentRegion',
  'body',
  'cta',
  // Hero Banner Block (CF-01) parts
  'wrapper',
  'backgroundWrapper',
  'backgroundMedia',
  'contentWrapper',
  'contentWrapperOuter',
  'title',
  'subtitle',
  'actionLinks',
  // Hero Banner Block (CF-04) parts
  'bottomBar',
  'leftCta',
  'rightCta',
  // Hero Banner Block (CF-06) parts
  'mediaWrapper',
  'contentSection',
  'titleColumn',
  'subtitleColumn'
]);

/**
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {import('@chakra-ui/styled-system').PartsStyleInterpolation<{
 *  keys: ("root" | "grid" | "mediaRegion" | "contentRegion" | "body" | "cta")[]
 * }>} HeroBannerStyles
 */
export const heroBannerStyles = helpers.defineMultiStyleConfig({
  /**
   * @param {Object} args - Component arguments
   * @param {Contentful.HeroBanner} args.component - HeroBanner component data
   * @param {boolean} [args.isComplete] - Whether component has all fields
   * @param {boolean} [args.isFetching] - Whether additional data is being fetched
   *
   * @returns {HeroBannerStyles} - Component styles
   */
  baseStyle: ({component}) => {
    return {
      root: {
        backgroundImage: '',
      },
    };
  },
  variants: {
    /**
     * @param {Object} args - Component arguments
     * @param {Contentful.HeroBanner} args.component - HeroBanner component data
     * @param {boolean} [args.isComplete] - Whether component has all fields
     * @param {boolean} [args.isFetching] - Whether additional data is being fetched
     *
     * @returns {HeroBannerStyles} - Component styles
     */
    'Callout Block - Split (CF-023)': (args) => ({
      root: {
        padding: {base: '20px 0', lg: '0 80px'},
        maxWidth: {base: '100%', lg: '1440px'},
        marginX: 'auto',
      },

      grid: {
        templateColumns: {base: '1fr', lg: '1fr 1fr'},
        position: 'relative',
        overflow: 'hidden',
      },

      contentRegion: {
        background: {base: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.50) 44.28%)', lg: 'rgba(27, 26, 24, 0.90)'},
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        gap: {base: '30px', lg: '24px'},
        justifyContent: {base: 'center', lg: 'flex-end'},
        padding: {base: '40px 20px', lg: '48px 72px 48px 48px'},
        zIndex: 2,
        sx: {
          '.link--outlined-dark': {
            color: '#C6B5A1',
            borderColor: '#C6B5A1',
          },
        },
      },

      mediaRegion: {
        left: {base: '50%', lg: 'unset'},
        position: {base: 'absolute', lg: 'relative'},
        top: {base: '50%', lg: 'unset'},
        transform: {base: 'translate(-50%, -50%)', lg: 'unset'},
        width: {base: '100%', lg: 'auto'},
        zIndex: 1,
      },

      body: {
        sx: {
          '.amb-markup': {
            'display': 'flex',
            'flexDirection': 'column',
            'gap': '16px',
            'textAlign': {base: 'center', lg: 'left'},

            'h1, h2, h3, h4, h5, h6': {
              color: '#C6B5A1',
              fontSize: '10px',
              fontWeight: '600',
              letterSpacing: '2px',
              lineHeight: '120%',
              marginBottom: 0,
              textTransform: 'uppercase',
            },

            '.chakra-text': {
              fontSize: {base: '20px', lg: '28px'},
              fontWeight: '500',
              letterSpacing: '-0.28px',
              lineHeight: '120%',

              _last: {
                marginBottom: 0,
              },
            },
          },
        },
      },

      cta: {},
    }),

    /**
     * Callout Block - Overlay (CF-024)
     * Full-width background image/video with centered subtitle and CTA overlay
     *
     * @param {Object} args - Component arguments
     * @param {Contentful.HeroBanner} args.component - HeroBanner component data
     * @param {boolean} [args.isComplete] - Whether component has all fields
     * @param {boolean} [args.isFetching] - Whether additional data is being fetched
     *
     * @returns {HeroBannerStyles} - Component styles
     */
    'Callout Block - Overlay (CF-024)': (args) => ({
      root: {
        padding: {base: '0 20px', lg: '0 80px'},
        maxWidth: {base: '100%', lg: '1440px'},
        marginX: 'auto',
      },
      wrapper: {
        position: 'relative',
        width: '100%',
        minHeight: {base: '400px', lg: '600px'},
        overflow: 'hidden',
      },

      backgroundWrapper: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,

        // Gradient overlay for text readability
        _after: {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.40) 100%)',
          zIndex: 1,
        },
      },

      backgroundMedia: {
        width: '100%',
        height: '100%',

        sx: {
          'img, video': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          },
        },
      },

      contentWrapper: {
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        height: '100%',
        minHeight: {base: '400px', lg: '600px'},
        padding: {base: '40px 20px', md: '60px 80px', lg: '80px 120px'},
        gap: {base: '24px', lg: '32px'},
      },

      subtitle: {
        color: 'white',
        fontFamily: 'heading',
        fontSize: {base: '24px', lg: '40px'},
        fontWeight: '500',
        lineHeight: '120%',
        letterSpacing: '-0.02em',
        margin: 0,
        maxWidth: {base: '100%', lg: '600px'},
      },

      actionLinks: {
        marginTop: {base: '8px', lg: '12px'},
        sx: {
          '.chakra-stack': {
            justifyContent: 'center',
          },
        },
      },
    }),

    /**
     * Hero Banner Block (CF-01)
     * Full-width hero with background image/video and overlay content
     * Supports underlay mode to go behind header
     *
     * @param {Object} args - Component arguments
     * @param {Contentful.HeroBanner} args.component - HeroBanner component data
     * @param {boolean} [args.isComplete] - Whether component has all fields
     * @param {boolean} [args.isFetching] - Whether additional data is being fetched
     *
     * @returns {HeroBannerStyles} - Component styles
     */
    'Hero Banner Block (CF-01)': ({component}) => {
      const isUnderlay = component?.underlay === true;

      return {
        wrapper: {
          position: 'relative',
          overflow: 'hidden',
          // Underlay mode: full viewport width/height, pull behind header
          ...(isUnderlay ? {
            width: '100%',
            height: '100vh',
            minHeight: {base: '600px', md: '700px', lg: '810px'},
            // Pull the hero up behind the header (header + above-header heights)
            mt: {base: '-140px', md: '-160px', lg: '-100px'},
          } : {
            width: '100%',
            minHeight: {base: '500px', md: '600px', lg: '720px'},
          }),
        },

        backgroundWrapper: {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,

          // Gradient overlay for text readability
          _after: {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.00) 100%)',
            zIndex: 1,
          },
        },

        backgroundMedia: {
          width: '100%',
          height: '100%',

          sx: {
            'img, video': {
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            },
          },
        },

        contentWrapperOuter: {
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          width: '100%',
          maxWidth: {base: '100%', lg: '1440px'},
          marginX: 'auto',
          height: '100%',
        },
        contentWrapper: {
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          height: '100%',
          gap: {base: '18px', md: '20px'},
          // Underlay mode: different padding and sizing
          ...(isUnderlay ? {
            padding: {base: '40px 20px', lg: '80px 0 80px 128px'},
            maxWidth: {base: '100%', md: '550px', lg: '600px'},
          } : {
            minHeight: {base: '500px', md: '600px', lg: '720px'},
            padding: {base: '40px 20px', md: '60px 40px', lg: '80px 76px'},
            maxWidth: {base: '100%', md: '600px', lg: '700px'},
          }),
        },

        title: {
          color: 'white',
          fontFamily: 'heading',
          fontSize: {base: '40px', lg: '60px'},
          fontWeight: '400',
          lineHeight: {base: '110%', lg: '120%'},
          letterSpacing: {base: '-0.04px', lg: '-0.06px'},
          margin: 0,
        },

        subtitle: {
          color: 'white',
          fontSize: {base: '14px', lg: '16px'},
          fontWeight: '400',
          lineHeight: '160%',
          margin: 0,
        },

        actionLinks: {
          marginTop: {base: '8px', lg: '12px'},
          sx: {
            'a, button': {
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '120%',
              padding: {base: '14px 48px', lg: '16px 48px'},
            },
          },
        },
      };
    },

    /**
     * Hero Banner Block (CF-04)
     * Full-viewport hero with background image/video, title overlay, and bottom action bar
     * Supports underlay mode and split CTA layout (left navigation + right actions)
     *
     * @param {Object} args - Component arguments
     * @param {Contentful.HeroBanner} args.component - HeroBanner component data
     * @param {boolean} [args.isComplete] - Whether component has all fields
     * @param {boolean} [args.isFetching] - Whether additional data is being fetched
     *
     * @returns {HeroBannerStyles} - Component styles
     */
    'Hero Banner Block (CF-04)': ({component}) => {
      const isUnderlay = component?.underlay === true;
      const hasRightCta = component?.rightCta && (Array.isArray(component.rightCta) ? component.rightCta.length > 0 : true);

      return {
        root: {
          padding: 0,
        },
        wrapper: {
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          // Underlay mode: full viewport width/height, pull behind header
          ...(isUnderlay ? {
            width: '100vw',
            left: '50%',
            right: '50%',
            marginLeft: '-50vw',
            marginRight: '-50vw',
            height: '100vh',
            minHeight: {base: '600px', md: '700px', lg: '810px'},
            // Pull the hero up behind the header (header + above-header heights)
            mt: {base: '-140px', md: '-160px', lg: '-100px'},
          } : {
            width: '100%',
            minHeight: {base: '500px', md: '600px', lg: '720px'},
          }),
        },

        backgroundWrapper: {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,

          // Gradient overlay for text readability
          _after: {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.30) 100%)',
            zIndex: 1,
          },
        },

        backgroundMedia: {
          width: '100%',
          height: '100%',

          sx: {
            'img, video': {
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            },
          },
        },

        contentWrapper: {
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          flex: 1,
          padding: {base: '32px 20px', lg: '33px 80px'},
          paddingBottom: {base: '20px', lg: '40px'},
          gap: {base: '8px', lg: '12px'},
          maxWidth: {base: '100%', lg: '1440px'},
          marginX: 'auto',
          width: '100%',
        },

        title: {
          color: 'white',
          fontFamily: 'heading',
          fontStyle: 'italic',
          fontSize: {base: '40px', lg: '96px'},
          fontWeight: '400',
          lineHeight: '100%',
          letterSpacing: '-0.02em',
          margin: 0,
          marginBottom: {base: '10px', lg: '52px'},
        },

        subtitle: {
          color: 'white',
          fontSize: {base: '14px', lg: '16px'},
          fontWeight: '400',
          lineHeight: '150%',
          margin: 0,
        },

        bottomBar: {
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: hasRightCta ? 'space-between' : 'flex-start',
          width: '100%',
          padding: {base: '8px 12px', lg: '15px 32px'},
          backgroundColor: 'rgba(27, 26, 24, 0.90)',
          gap: {base: '16px', lg: '24px'},
          flexWrap: {base: 'wrap', lg: 'nowrap'},
          borderRadius: '4px',
          background: 'linear-gradient(0deg, rgba(27, 26, 24, 0.20) 0%, rgba(27, 26, 24, 0.20) 100%), rgba(255, 255, 255, 0.10)',
          backdropFilter: 'blur(10px)',
        },

        leftCta: {
          display: {base: 'none', lg: 'flex'},
          alignItems: 'center',
          gap: {base: '16px', lg: '32px'},
          flexWrap: 'wrap',

          sx: {
            // Style navigation links
            'a, button': {
              color: 'white',
              fontSize: {base: '13px', lg: '13px'},
              fontWeight: '500',
              textDecoration: 'none',
              whiteSpace: 'nowrap',

              _hover: {
                textDecoration: 'underline',
              },
            },
            '.chakra-link': {
              fontSize: {base: '13px', lg: '13px'},
              fontWeight: '500',
              color: 'white',
              borderColor: 'transparent',
              backgroundColor: 'transparent',
              paddingLeft: '0',
              paddingRight: '0',
              _hover: {
                color: 'white',
                textDecoration: 'underline',
                backgroundColor: 'transparent',
                borderColor: 'transparent',
              },
            },
            '.chakra-stack': {
              alignItems: 'center',
              gap: {base: '16px', lg: '32px'},
            },
          },
        },

        rightCta: {
          display: 'flex',
          alignItems: 'center',
          gap: {base: '12px', lg: '16px'},
          marginLeft: {base: 'auto', lg: 'auto'},
          marginRight: {base: 'auto', lg: '0'},

          sx: {
            // Style action buttons
            'a, button': {
              alignItems: 'center',
              color: 'white',
              display: 'inline-flex',
              fontSize: {base: '14px', lg: '14px'},
              fontWeight: '600',
              padding: {base: '14px 48px', lg: '14px 48px'},
              gap: '8px',
              whiteSpace: 'nowrap',
            },
            '.link--base': {
              fontSize: {base: '13px', lg: '13px'},
              fontWeight: '500',
              padding: '14px 16px',
            },
            '.chakra-stack': {
              alignItems: 'center',
              flexWrap: 'wrap',
            },
          },
        },
      };
    },

    /**
     * Hero Banner Block (CF-06)
     * Background image/video on top with content section below (title left, subtitle right)
     *
     * @param {Object} args - Component arguments
     * @param {Contentful.HeroBanner} args.component - HeroBanner component data
     * @param {boolean} [args.isComplete] - Whether component has all fields
     * @param {boolean} [args.isFetching] - Whether additional data is being fetched
     *
     * @returns {HeroBannerStyles} - Component styles
     */
    'Hero Banner Block (CF-06)': ({component}) => {
      const isUnderlay = component?.underlay === true;

      return {
        wrapper: {
          position: 'relative',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'brandColors.secondaryBG',
          // Underlay mode: full viewport width, pull behind header
          ...(isUnderlay ? {
            width: '100vw',
            left: '50%',
            right: '50%',
            marginLeft: '-50vw',
            marginRight: '-50vw',
            // Pull the hero up behind the header (header + above-header heights)
            mt: {base: '-140px', lg: '-100px'},
          } : {}),
        },

        mediaWrapper: {
          position: 'relative',
          width: '100%',
          aspectRatio: {base: '375/468', lg: '1440/535'},
          overflow: 'hidden',
        },

        backgroundMedia: {
          width: '100%',
          height: '100%',

          sx: {
            'img, video': {
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            },
          },
        },

        contentSection: {
          display: 'flex',
          flexDirection: {base: 'column', lg: 'row'},
          alignItems: 'flex-start',
          gap: {base: '24px', lg: '150px'},
          width: '100%',
          maxWidth: {base: '100%', lg: '1440px'},
          marginX: 'auto',
          px: {base: '20px', lg: '80px'},
          py: {base: '36px', lg: '120px'},
          pb: {base: '36px', lg: '48px'},
        },

        titleColumn: {
          width: {base: '100%', lg: '40%'},
          flexShrink: 0,
        },

        title: {
          color: 'brandColors.greyscale90',
          fontFamily: 'heading',
          fontSize: {base: '40px', lg: '72px'},
          fontWeight: {base: '500', lg: '400'},
          lineHeight: {base: '120%', lg: '100%'},
          letterSpacing: {base: '-0.4px', lg: '-0.72px'},
          margin: 0,
        },

        subtitleColumn: {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: {base: 'center', lg: 'flex-start'},
          alignItems: 'flex-start',
          flex: '1 0 0',
          pt: {base: 0, lg: '14px'},
        },

        subtitle: {
          color: 'brandColors.greyscale90',
          fontSize: {base: '16px', lg: '20px'},
          fontWeight: '400',
          lineHeight: {base: '150%', lg: '160%'},
          margin: 0,
        },
      };
    },
  },
});
